import { Platform } from 'react-native'

import { CurrencyKind } from '@/types/wallet'

export type RuntimeEnvironment = 'production' | 'staging' | 'localhost'
export type ActiveRuntimeEnvironment = RuntimeEnvironment | 'configured'

export interface RuntimeEnvironmentConfig {
  apiUrl: string
  publicEnvironment: 'prod' | 'staging' | 'develop'
  serverDomain: string
}

export const RUNTIME_ENVIRONMENTS: Record<RuntimeEnvironment, RuntimeEnvironmentConfig> = {
  production: {
    apiUrl: 'https://api.baobiweb.com',
    publicEnvironment: 'prod',
    serverDomain: 'baobiweb.com'
  },
  staging: {
    apiUrl: 'https://devapi.baobiweb.com',
    publicEnvironment: 'staging',
    serverDomain: 'baobiweb.com'
  },
  localhost: {
    apiUrl: Platform.OS === 'android' ? 'http://10.0.2.2:3030' : 'http://localhost:3030',
    publicEnvironment: 'develop',
    serverDomain: Platform.OS === 'android' ? '10.0.2.2' : 'localhost'
  }
}

const bundledApiUrl = process.env.EXPO_PUBLIC_API_ENDPOINT
const configuredRuntimeEnvironment: RuntimeEnvironmentConfig = {
  apiUrl: bundledApiUrl ?? RUNTIME_ENVIRONMENTS.localhost.apiUrl,
  publicEnvironment: 'develop',
  serverDomain: process.env.EXPO_PUBLIC_SERVER_DOMAIN ?? process.env.EXPO_PUBLIC_API_HOST ?? ''
}

function getBundledEnvironment(): ActiveRuntimeEnvironment {
  switch (process.env.EXPO_PUBLIC_ENV) {
    case 'prod':
    case 'production':
      return 'production'
    case 'staging':
      return 'staging'
    case 'develop':
      return bundledApiUrl && bundledApiUrl !== RUNTIME_ENVIRONMENTS.localhost.apiUrl ? 'configured' : 'localhost'
    default:
      return (
        (Object.entries(RUNTIME_ENVIRONMENTS).find(([, config]) => config.apiUrl === bundledApiUrl)?.[0] as
          | RuntimeEnvironment
          | undefined) ?? (bundledApiUrl ? 'configured' : 'localhost')
      )
  }
}

export const DEFAULT_RUNTIME_ENVIRONMENT = getBundledEnvironment()

let runtimeEnvironment = DEFAULT_RUNTIME_ENVIRONMENT
let runtimeConfig =
  runtimeEnvironment === 'configured' ? configuredRuntimeEnvironment : RUNTIME_ENVIRONMENTS[runtimeEnvironment]

export let PUBLIC_ENV = runtimeConfig.publicEnvironment

export const COUNTRY_CODES = {
  China: { dial_code: '86', code: 'CN', flag: '🇨🇳' },
  Taiwan: { dial_code: '886', code: 'TW', flag: '🇹🇼' },
  'Hong Kong': { dial_code: '852', code: 'HK', flag: '🇭🇰' },
  Thailand: { dial_code: '66', code: 'TH', flag: '🇹🇭' },
  'United States': { dial_code: '1', code: 'US', flag: '🇺🇸' },
  Russia: { dial_code: '7', code: 'RU', flag: '🇷🇺' },
  Netherlands: { dial_code: '31', code: 'NL', flag: '🇳🇱' },
  France: { dial_code: '33', code: 'FR', flag: '🇫🇷' },
  Cyprus: { dial_code: '357', code: 'CY', flag: '🇨🇾' },
  'United Kingdom': { dial_code: '44', code: 'GB', flag: '🇬🇧' },
  Poland: { dial_code: '48', code: 'PL', flag: '🇵🇱' },
  Malaysia: { dial_code: '60', code: 'MY', flag: '🇲🇾' },
  Australia: { dial_code: '61', code: 'AU', flag: '🇦🇺' },
  Indonesia: { dial_code: '62', code: 'ID', flag: '🇮🇩' },
  Philippines: { dial_code: '63', code: 'PH', flag: '🇵🇭' },
  Singapore: { dial_code: '65', code: 'SG', flag: '🇸🇬' },
  Japan: { dial_code: '81', code: 'JP', flag: '🇯🇵' },
  Korea: { dial_code: '82', code: 'KR', flag: '🇰🇷' },
  Macao: { dial_code: '853', code: 'MO', flag: '🇲🇴' },
  Cambodia: { dial_code: '855', code: 'KH', flag: '🇰🇭' },
  Turkey: { dial_code: '90', code: 'TR', flag: '🇹🇷' },
  Myanmar: { dial_code: '95', code: 'MM', flag: '🇲🇲' },
  'United Arab Emirates': { dial_code: '971', code: 'AE', flag: '🇦🇪' },
  Israel: { dial_code: '972', code: 'IL', flag: '🇮🇱' }
}
/**
 * TODO: localhost cannot work on Android emulator
 * iOS is running in a simulator and Android is running in an emulator
 * The emulator emulates a real device while the simulator is only imitating the device.
 * Therefore the localhost on Android is pointing to the emulated Android device. And not to the machine on which your server is running.
 */
// export const SERVER_DOMAIN = (() => {
//   const DOMAINS = {
//     LOCALHOST: 'localhost',
//     ANDROID_EMULATOR: '10.0.2.2',
//     GENYMOTION: '10.0.3.2'
//   }

//   return __DEV__
//     ? Platform.OS === 'ios' || Platform.OS === 'web'
//       ? DOMAINS.LOCALHOST
//       : DOMAINS.ANDROID_EMULATOR
//     : 'api.yubiservices.com'
// })()

export let SERVER_DOMAIN = runtimeConfig.serverDomain
export let BASE_URL = runtimeConfig.apiUrl
export const LIVECHAT_LICENSE = process.env.EXPO_PUBLIC_LIVECHAT_LICENSE ?? ''

export function configureRuntimeEnvironment(environment: ActiveRuntimeEnvironment) {
  runtimeEnvironment = environment
  runtimeConfig = environment === 'configured' ? configuredRuntimeEnvironment : RUNTIME_ENVIRONMENTS[environment]
  PUBLIC_ENV = runtimeConfig.publicEnvironment
  SERVER_DOMAIN = runtimeConfig.serverDomain
  BASE_URL = runtimeConfig.apiUrl
}

export function getRuntimeEnvironment() {
  return runtimeEnvironment
}

export function getRuntimeConfig() {
  return runtimeConfig
}

export const WEB_VIEW_MAX_WIDTH = 540

export const SUPPORTED_CURRENCIES = [CurrencyKind.YBB]
